import React, { useState, useRef, useEffect } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";

import { API_BASE, SHEETS, forceRefreshSheet } from "../utils/api";
import GstSearchPopup from "../components/GstSearchPopup";
import { isAdminPhone, isStaffRole, startAdminSession, clearAdminSession } from "../utils/adminView";

// Used only for the bypass/test login flow which needs the full dealers
// list. Real login goes through the phone-filtered endpoint below — much
// smaller response (1 row instead of hundreds), 10x faster.
const USERS_URL = SHEETS.dealers();

const OTP_SEND_URL = `${API_BASE}/api/auth/login`;
const OTP_VERIFY_URL = `${API_BASE}/api/auth/verify-otp`;

const PRODUCTS_URL = SHEETS.products;

const RINGS_URL = SHEETS.rings;

const PRODUCTS_CACHE_KEY = "products_cache_v2";
const PRODUCTS_CACHE_TS_KEY = "products_cache_ts_v2";
const RINGS_CACHE_KEY = "rings_cache_v2";
const RINGS_CACHE_TS_KEY = "rings_cache_ts_v2";
const MASTER_CACHE_TTL = 30 * 1000; // 30 sec

function isFresh(tsKey, ttl = MASTER_CACHE_TTL) {
  const ts = Number(localStorage.getItem(tsKey) || 0);
  return ts && Date.now() - ts < ttl;
}

// Apps Script endpoints — one per product type. Login warms a combined
// cache so the Pending Orders / Order History popups on the dashboard
// open INSTANTLY. Each row is tagged with `__type` so the popup tabs
// filter reliably regardless of order-number formatting.
const ORDER_PREFETCH_SOURCES = [
  {
    type: "structure",
    url: "https://script.google.com/macros/s/AKfycbzjLNACRiEADmqzbdte-Iiloop_XNOt5D4nVASKlcITbazizWyo2chRotGqOVs5iCxW/exec",
  },
  {
    type: "6mm",
    url: "https://script.google.com/macros/s/AKfycbyj4pVlyIZ8vU4iITHmCPfsMpE8L-7sVIniSlppFlgC5JIVyI2846YFIbDkKKlDs6A/exec",
  },
  {
    type: "binding",
    url: "https://script.google.com/macros/s/AKfycbzXdSGtC_XaArAZmIWbAMXoyrXk7HfUyU3bHms93vnpLF9PjUGq_whwpDyH-MUdQzM/exec",
  },
  {
    type: "rings",
    url: "https://script.google.com/macros/s/AKfycbzCPewSCHAOyB14OBMUgOTfSNvwsvjbkpHKCevFQ9KiOFWPA_2eIq3eoJNuKCcfocjiBw/exec",
  },
];

// Hit all 4 product-type endpoints in parallel, tag rows with __type,
// and dump the merged result into the shared ordersCombined:v1 cache.
// Pure fire-and-forget — never throws, never blocks the login flow.
// eslint-disable-next-line no-unused-vars
function warmOrdersCache() {
  Promise.all(
    ORDER_PREFETCH_SOURCES.map((src) =>
      fetch(src.url, { cache: "no-store" })
        .then((r) => r.json())
        .then((d) => {
          const arr = Array.isArray(d)
            ? d
            : Array.isArray(d?.data)
              ? d.data
              : [];
          return arr.map((row) =>
            row && typeof row === "object" ? { ...row, __type: src.type } : row
          );
        })
        .catch(() => [])
    )
  )
    .then((results) => {
      const merged = results.flat();
      try {
        localStorage.setItem(
          "ordersCombined:v2",
          JSON.stringify({ rows: merged, ts: Date.now() })
        );
      } catch {
        /* quota — non-fatal */
      }
    })
    .catch(() => {});

  // Also pre-fetch the outstanding sheet so the popup opens instantly.
  fetch(
    "https://script.google.com/macros/s/AKfycbx1rC5DE6enjBU2zhhGuV-2dIQ_CZU6F0fA0yJlRt6fNdwsON5-yYqHijbuMubGYszb/exec",
    { cache: "no-store" }
  )
    .then((r) => r.json())
    .then((d) => {
      const arr = Array.isArray(d) ? d : Array.isArray(d?.data) ? d.data : [];
      try {
        localStorage.setItem(
          "outstanding:v1",
          JSON.stringify({ rows: arr, ts: Date.now() })
        );
      } catch {
        /* quota — non-fatal */
      }
    })
    .catch(() => {});
}

async function prefetchMasterData() {
  const jobs = [];

  if (!isFresh(PRODUCTS_CACHE_TS_KEY)) {
    jobs.push(
      fetch(PRODUCTS_URL)
        .then((r) => r.json())
        .then((data) => {
          localStorage.setItem(PRODUCTS_CACHE_KEY, JSON.stringify(data || []));
          localStorage.setItem(PRODUCTS_CACHE_TS_KEY, String(Date.now()));
        })
        .catch(() => {}),
    );
  }

  if (!isFresh(RINGS_CACHE_TS_KEY)) {
    jobs.push(
      fetch(RINGS_URL)
        .then((r) => r.json())
        .then((data) => {
          localStorage.setItem(RINGS_CACHE_KEY, JSON.stringify(data || []));
          localStorage.setItem(RINGS_CACHE_TS_KEY, String(Date.now()));
        })
        .catch(() => {}),
    );
  }

  await Promise.allSettled(jobs);
}

const normalizeType = (t) =>
  String(t || "")
    .trim()
    .toLowerCase();

/** USERS CACHE **/
// Bumped to v2 — forces existing users to discard any stale cached list
// from before the force-refresh fix. Drop TTL so login always hits a fresh
// dealers list after a force-refresh of the backend sheet cache.
const USERS_CACHE_KEY = "users_cache_v2";
const USERS_CACHE_TS_KEY = "users_cache_ts_v2";
const USERS_CACHE_TTL = 0; // always fetch fresh on login

// Fast read — hits the backend's MongoDB cache, server-side filtered to
// just the requested phone number. Response is typically 1 row instead
// of the full dealers list (hundreds of rows), making this WAY faster.
async function readUserByMobile(mobile) {
  const url = SHEETS.dealers(mobile);
  const res = await fetch(url, { cache: "no-store" });
  const rows = await res.json();
  return Array.isArray(rows) ? rows : [];
}

// Find a user by their 10-digit mobile in the supplied list. Used as a
// final filter step because the server matches the LAST 10 digits — we
// confirm an exact match here.
function findByMobile(users, mobile) {
  return (users || []).find(
    (u) => String(u["Contact No"] ?? "").trim() === mobile,
  );
}

// Strategy:
//   1. Read users from backend cache (fast — ~100ms from MongoDB).
//   2. If the user's phone is found → done.
//   3. If NOT found → kick off an ASYNC backend refresh (returns
//      instantly now — backend fires the upstream Apps Script fetch in
//      the background) and POLL the cache for up to ~10s waiting for the
//      new row to land. Covers freshly-added dealers.
//
// Returning users get an instant login. Brand-new dealers (added in the
// last 2 minutes) wait at most a few seconds while the backend pulls
// fresh data from Apps Script.
// Build the cart-compatible firm-cache payload from raw dealer rows. Cart
// reads this from localStorage under `firmsCache:v1:${phone}` and skips
// its own network fetch if the timestamp is fresh.
export function buildCartFirmsCachePayload(rows, mobile, found) {
  const norm = (v) => String(v ?? "").trim();
  const num = (v) =>
    Number(
      String(v ?? "")
        .replace(/,/g, "")
        .trim(),
    ) || 0;
  const mappedFirms = (rows || [])
    .map((r) => ({
      firmName:
        norm(
          r?.["Particulars"] ||
            r?.["Firm Name"] ||
            r?.["Firm Name "] ||
            r?.firmName ||
            r?.FirmName,
        ) || "",
      gst:
        norm(
          r?.["GSTIN/UIN"] ||
            r?.["GST No"] ||
            r?.["GST No "] ||
            r?.gst ||
            r?.GST,
        ) || "",
      contact:
        norm(
          r?.["Contact No"] ||
            r?.["Contact No "] ||
            r?.contact ||
            r?.phone ||
            r?.Phone,
        ) || "",
      creditLimit: String(num(r?.["Credit Limit In Lakh"]) * 100000),
      outstanding:
        norm(
          r?.["Outstanding"] ||
            r?.["Outstanding "] ||
            r?.outstanding ||
            r?.OutStanding,
        ) || "0",
      contactPerson:
        norm(
          r?.["Contact Person Name"] ||
            r?.["Contact Person"] ||
            r?.["Contact Person "] ||
            r?.contactPerson ||
            r?.ContactPerson,
        ) || "",
      area: norm(r?.Area || r?.area || "") || "",
      district:
        norm(
          r?.District ||
            r?.district ||
            r?.DISTRICT ||
            r?.["District"] ||
            r?.["District "] ||
            r?.["STR District"] ||
            r?.["SDistrict"],
        ) || "",
    }))
    .filter((f) => f.firmName);
  // Dedupe by firmName+gst (matches CartPopup's dedupe key)
  const seen = new Set();
  const uniq = [];
  for (const f of mappedFirms) {
    const key = `${f.firmName}__${f.gst}`;
    if (!seen.has(key)) {
      seen.add(key);
      uniq.push(f);
    }
  }
  return {
    rows: rows || [],
    firms: uniq,
    profile: found || null,
    loginPhone: mobile,
  };
}

async function getUsersForMobile(mobile) {
  const users = await readUserByMobile(mobile);
  const found = findByMobile(users, mobile);
  // Fire-and-forget — if the user isn't in cache, ask the backend to pull
  // fresh data so the NEXT login attempt finds them. We DON'T wait for
  // it — the backend's periodic 120s sync handles 99% of cases anyway,
  // and Apps Script latency was making logins unusably slow.
  if (!found) {
    forceRefreshSheet("dealers").catch(() => {});
  }
  localStorage.setItem(USERS_CACHE_KEY, JSON.stringify(users));
  localStorage.setItem(USERS_CACHE_TS_KEY, String(Date.now()));

  // PRE-WARM the cart's firms cache so the first cart open after login
  // doesn't trigger another dealers fetch. Same data, same shape — cart
  // reads this and skips its network call when the ts is < 90s old.
  if (found && users.length > 0) {
    try {
      const payload = buildCartFirmsCachePayload(users, mobile, found);
      localStorage.setItem(
        `firmsCache:v1:${mobile}`,
        JSON.stringify({ ts: Date.now(), payload }),
      );
    } catch {
      /* ignore — fresh write isn't critical */
    }
  }

  return { users, found };
}

// Backwards-compatible export for the bypass-OTP test login path (which
// needs the full dealers list, not a specific lookup). Hits cache only —
// no Apps Script round-trip.
async function getUsersCached() {
  const res = await fetch(USERS_URL, { cache: "no-store" });
  const users = await res.json();
  return Array.isArray(users) ? users : [];
}

/** BYPASS LOGIN SETTINGS **/
const BYPASS_PHONE = "9898989898";
const BYPASS_OTP = "1254";

export default function Login() {
  const [showOTP, setShowOTP] = useState(false);
  const [gstPopupOpen, setGstPopupOpen] = useState(false);
  const [mobile, setMobile] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [otpInputs, setOtpInputs] = useState(["", "", "", ""]); // 4-digit OTP
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [matchedUser, setMatchedUser] = useState(null);
  const [userType, setUserType] = useState("");
  const [showBypassOtpPopup, setShowBypassOtpPopup] = useState(false);

  const inputRefs = useRef([]);
  const navigate = useNavigate();

  const submitGoogleFormSilently = async (phone) => {
    const url =
      "https://docs.google.com/forms/d/e/1FAIpQLSdaWfvkjgfp1UPuLzP_oyozTRtDpog9naWxpQXs9iQSw9uRKQ/formResponse";

    const formData = new FormData();
    formData.append("entry.1509690443", phone);

    try {
      await fetch(url, {
        method: "POST",
        body: formData,
        mode: "no-cors",
      });
    } catch {}
  };

  useEffect(() => {
    if (showOTP) {
      setTimeout(() => inputRefs.current[0]?.focus(), 50);
    }
  }, [showOTP]);

  useEffect(() => {
    getUsersCached().catch(() => {});
  }, []);

  /* ─── Quick Win #3 — Prefetch as user types ─────────────────────
     Kick off the dealer lookup the moment the user reaches 6 digits.
     By the time they finish typing 10 digits (1-3 sec later), the
     row is already in the browser's HTTP cache + localStorage —
     "Send OTP" click feels instant. Debounced + de-duped so we don't
     flood the backend.                                              */
  const prefetchedRef = useRef(new Set());
  useEffect(() => {
    if (mobile.length < 6 || !/^\d+$/.test(mobile)) return;
    const phoneToFetch = mobile.slice(-10);
    if (phoneToFetch.length < 6) return;
    if (prefetchedRef.current.has(phoneToFetch)) return;
    prefetchedRef.current.add(phoneToFetch);

    const t = setTimeout(() => {
      // Warm the HTTP cache + localStorage in parallel.
      readUserByMobile(phoneToFetch)
        .then((rows) => {
          if (!Array.isArray(rows) || rows.length === 0) return;
          try {
            localStorage.setItem(USERS_CACHE_KEY, JSON.stringify(rows));
            localStorage.setItem(USERS_CACHE_TS_KEY, String(Date.now()));
            const matchedRow = findByMobile(rows, phoneToFetch);
            if (matchedRow) {
              const payload = buildCartFirmsCachePayload(
                rows,
                phoneToFetch,
                matchedRow,
              );
              localStorage.setItem(
                `firmsCache:v1:${phoneToFetch}`,
                JSON.stringify({ ts: Date.now(), payload }),
              );
            }
          } catch {
            /* ignore */
          }
        })
        .catch(() => {});
    }, 250); // small debounce so we don't fire on every keystroke
    return () => clearTimeout(t);
  }, [mobile]);

  const handleSendOTP = async () => {
    if (mobile.length !== 10 || !/^\d+$/.test(mobile)) {
      toast.error("Enter valid 10-digit mobile number");
      return;
    }

    if (mobile.startsWith("0") || mobile.startsWith("99999")) {
      toast.error("This mobile number is not allowed to login");
      return;
    }

    // Record EVERY login attempt's phone in the usercount sheet — registered
    // OR unregistered, whether or not an OTP is sent. The sheet's own script
    // fills the remaining columns (GST/firm/etc.) automatically.
    submitGoogleFormSilently(mobile);

    setLoading(true);

    try {
      // BYPASS FLOW
      if (mobile === BYPASS_PHONE) {
        let found = null;

        try {
          const users = await getUsersCached();
          found = (users || []).find((u) => {
            const phone = String(u["Contact No"] ?? "").trim();
            return phone === mobile;
          });
        } catch {}

        const detectedType = found ? normalizeType(found["Type"]) : "dealer";

        setMatchedUser(
          found || {
            "Contact No": mobile,
            Name: "Bypass User",
            Type: detectedType,
          },
        );
        setUserType(detectedType);

        setShowOTP(true);
        setShowBypassOtpPopup(true);
        toast.success("OTP generated (Test Login)!");
        setMessage("OTP generated (Test Login)!");
        return;
      }

      // NORMAL FLOW — ONE round-trip: /login does dealer lookup +
      // OTP store + WhatsApp send all in a single request. Saves the
      // 300ms RTT we used to spend on the separate /dealers fetch.
      console.time("[login] total");
      console.time("[login] POST /api/auth/login (combined)");

      // Backend can take up to 25 sec to respond when sheet sync is
      // hammering MongoDB. 30 sec gives enough headroom but still bails
      // if something is truly broken. OTP fires from backend regardless
      // of when client sees the response (setImmediate after res.json).
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);
      let data;
      try {
        const res = await fetch(OTP_SEND_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ phone: mobile }),
          signal: controller.signal,
        });
        data = await res.json();
      } catch (e) {
        console.warn("[login] OTP send timed out (3s) — using cache fallback");
        // Network blip — fall back to localStorage cache lookup so
        // returning users with cached data can still log in.
        try {
          const cachedUsers = JSON.parse(
            localStorage.getItem(USERS_CACHE_KEY) || "[]",
          );
          const cachedFound = findByMobile(cachedUsers, mobile);
          data = cachedFound
            ? { success: true, found: true, dealer: cachedFound, dealerRows: [cachedFound] }
            : { success: false, message: "Network error" };
        } catch {
          data = { success: false, message: "Network error" };
        }
      } finally {
        clearTimeout(timeoutId);
      }
      console.timeEnd("[login] POST /api/auth/login (combined)");
      console.timeEnd("[login] total");

      if (!data.success) {
        // Unregistered number → open the GST-search popup.
        if (data.found === false) {
          setGstPopupOpen(true);
        } else {
          toast.error(data.message || "Login failed");
        }
        return;
      }

      // ── Back-compat: if backend is the OLD v6/v7 code (no combined
      //    lookup), it returns `{success, message, provider, phone}`
      //    WITHOUT `dealer` / `dealerRows`. Use cached dealers if
      //    available; if not, kick a 2-second-bounded fetch and
      //    proceed optimistically on timeout so the UI never hangs.
      let found = data.dealer;
      let dealerRows = Array.isArray(data.dealerRows) ? data.dealerRows : [];

      if (!found && !data.hasOwnProperty("dealer")) {
        // 1) Try localStorage cache first — instant, no network.
        try {
          const cached = JSON.parse(
            localStorage.getItem(USERS_CACHE_KEY) || "[]",
          );
          const cachedHit = findByMobile(cached, mobile);
          if (cachedHit) {
            found = cachedHit;
            dealerRows = cached;
            console.log("[login] backend old — using cached dealer");
          }
        } catch {
          /* ignore */
        }
        // 2) If still nothing, race a 2-sec fetch vs a timeout. We
        //    NEVER block the UI for more than 2 sec on this fallback.
        if (!found) {
          console.log("[login] backend old — quick dealers fetch (max 2s)");
          const racer = (async () => {
            const r = await getUsersForMobile(mobile);
            return r;
          })();
          const result = await Promise.race([
            racer.catch(() => ({ found: null, users: [] })),
            new Promise((resolve) =>
              setTimeout(() => resolve({ found: null, users: [], _timeout: true }), 2000),
            ),
          ]);
          if (result?._timeout) {
            console.warn(
              "[login] dealers fetch timed out — proceeding optimistically (backend overloaded)",
            );
            // OTP already sent — let user enter it. If they aren't
            // actually registered, verify-otp endpoint will reject.
            found = { "Contact No": mobile, _optimistic: true };
            dealerRows = [found];
          } else {
            found = result.found;
            dealerRows = Array.isArray(result.users) ? result.users : [];
          }
        }
      }

      if (!found) {
        // Backend confirmed dealer not in MongoDB cache.
        // Kick a background refresh so next attempt finds them.
        forceRefreshSheet("dealers").catch(() => {});
        // Open the GST-search popup so the user can find their registered
        // number or start a new registration.
        setGstPopupOpen(true);
        return;
      }

      // Persist dealer rows + warm caches BEFORE navigating, so the
      // dashboard popups (which read from firmsCache) open instantly.
      try {
        localStorage.setItem(USERS_CACHE_KEY, JSON.stringify(dealerRows));
        localStorage.setItem(USERS_CACHE_TS_KEY, String(Date.now()));
        const payload = buildCartFirmsCachePayload(dealerRows, mobile, found);
        localStorage.setItem(
          `firmsCache:v1:${mobile}`,
          JSON.stringify({ ts: Date.now(), payload }),
        );
      } catch {
        /* cache write failures are non-fatal */
      }

      const detectedType = data.role
        ? normalizeType(data.role)
        : normalizeType(found["Type"]);
      setMatchedUser(found);
      setUserType(detectedType);

      setShowOTP(true);
      toast.success("OTP sent successfully on WhatsApp!");
      setMessage("OTP sent successfully on WhatsApp!");
    } catch {
      toast.error("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    const enteredOTP = otpInputs.join("");

    if (enteredOTP.length !== 4) {
      toast.error("Please enter full 4-digit OTP");
      return;
    }

    try {
      // BYPASS VERIFY
      if (mobile === BYPASS_PHONE) {
        if (enteredOTP !== BYPASS_OTP) {
          toast.error("Invalid OTP");
          return;
        }

        const userToStore = matchedUser || { phone: mobile };

        localStorage.setItem("dealer", JSON.stringify(userToStore));
        localStorage.setItem("role", userType || "dealer");
        // Store the ACTUAL number the user logged in with, so the auth
        // guard can verify that exact number against the dealers sheet.
        localStorage.setItem("loginPhone", String(mobile));
        localStorage.removeItem("loginAt");

        setShowSuccessPopup(true);
        // Navigate FIRST, warm caches afterwards (off the critical path).
        navigate("/login-success", { replace: true });
        setTimeout(() => {
          prefetchMasterData().catch(() => {});
        }, 50);

        return;
      }

      // NORMAL VERIFY
      const res = await fetch(OTP_VERIFY_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          phone: mobile,
          otp: enteredOTP,
          role: userType,
        }),
      });

      const data = await res.json();

      if (data.success) {
        const userToStore = matchedUser || data.dealer || { phone: mobile };

        localStorage.setItem("dealer", JSON.stringify(userToStore));
        localStorage.setItem("role", userType);
        // Store the ACTUAL number the user logged in with, so the auth
        // guard can verify that exact number against the dealers sheet.
        localStorage.setItem("loginPhone", String(mobile));
        localStorage.removeItem("loginAt");

        // Admin / NBD → first choose which dealer's dashboard to view.
        if (isAdminPhone(mobile) || isStaffRole(userType)) {
          startAdminSession(mobile, userToStore, userType, isAdminPhone(mobile) ? "all" : "nbd");
          navigate("/select-dealer", { replace: true });
          return;
        }
        clearAdminSession();

        setShowSuccessPopup(true);
        // Navigate FIRST so the transition is instant, then warm caches
        // off the critical path (they were ~6 fetches competing with the
        // navigation and making it feel slow).
        navigate("/login-success", { replace: true });
        setTimeout(() => {
          prefetchMasterData().catch(() => {});
        }, 50);
      } else {
        toast.error(data.message || "Invalid OTP");
      }
    } catch {
      toast.error("Verification failed");
    }
  };

  const handleResend = () => {
    setOtpInputs(["", "", "", ""]);
    toast.info("Resending OTP...");
    handleSendOTP();
  };

  const handleOtpChange = (value, index) => {
    if (!/^\d*$/.test(value)) return;

    const newOtp = [...otpInputs];
    newOtp[index] = value.slice(-1);
    setOtpInputs(newOtp);

    if (value && index < 3) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otpInputs[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e, index) => {
    e.preventDefault();
    const text = (e.clipboardData.getData("text") || "").trim();
    const digits = text.replace(/\D/g, "").slice(0, 4);

    if (!digits) return;

    const newOtp = [...otpInputs];

    if (digits.length === 4) {
      for (let i = 0; i < 4; i++) newOtp[i] = digits[i];
      setOtpInputs(newOtp);
      inputRefs.current[3]?.focus();
      return;
    }

    let ptr = index;
    for (let i = 0; i < digits.length && ptr < 4; i++, ptr++) {
      newOtp[ptr] = digits[i];
    }

    setOtpInputs(newOtp);

    const nextEmpty = newOtp.findIndex((d) => !d);
    (nextEmpty === -1
      ? inputRefs.current[3]
      : inputRefs.current[nextEmpty]
    )?.focus();
  };

  return (
    <>
      <ToastContainer
        position="top-center"
        autoClose={4000}
        hideProgressBar
        newestOnTop
        closeOnClick
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
      />

      <div
        className="fixed inset-0 bg-cover bg-center bg-no-repeat scale-105"
        style={{ backgroundImage: "url('/images/bg_friends.jpeg')" }}
      />
      <div className="fixed inset-0 bg-white/30 backdrop-blur-md" />

      <div className="relative min-h-screen flex items-center justify-start pt-12 px-4 sm:pt-16 md:pt-20">
        <div className="w-full max-w-md mx-auto">
          <div className="bg-white/95 backdrop-blur-lg rounded-2xl shadow-2xl overflow-hidden w-full mx-auto">
            <div className="p-6 sm:p-8 md:p-10 lg:p-12 text-center">
              <img
                src="/images/Logo_Thumbnail-removebg.png"
                alt="Logo"
                className="h-20 sm:h-24 mx-auto mb-4 drop-shadow-lg animate-float"
              />

              {/* Tagline — "registered Dealer" gets the attention treatment:
                  blue, underlined, and a soft heartbeat pulse so the user's
                  eye lands on the access-restriction immediately. The
                  surrounding sentence stays muted to keep contrast high.
                  Animation is honored by reduced-motion users via the
                  media query in the inlined <style> tag. */}
              <style>{`
                @keyframes sotaHeartbeat {
                  0%, 100% { transform: scale(1);   opacity: 1;   }
                  20%      { transform: scale(1.08); opacity: 1;   }
                  40%      { transform: scale(1);    opacity: 0.85; }
                  60%      { transform: scale(1.05); opacity: 1;   }
                }
                .sota-heartbeat {
                  display: inline-block;
                  animation: sotaHeartbeat 1.6s ease-in-out infinite;
                  transform-origin: center;
                }
                @media (prefers-reduced-motion: reduce) {
                  .sota-heartbeat { animation: none; }
                }
              `}</style>
              {/* Tagline — explicit line break before "using" so the
                  sentence reads exactly:
                    line 1:  Only "Registered Dealers" can login
                    line 2:  using their mobile number
                  Heartbeat lives on the highlighted span only so the
                  surrounding sentence stays still. */}
              <p className="text-gray-700 mt-3 text-base sm:text-lg font-medium font-['DM_Sans'] leading-relaxed text-center sota-heartbeat">
                Only{" "}
                <span className=" font-extrabold text-blue-600 decoration-blue-500 decoration-2 whitespace-nowrap">
                  &ldquo;Registered Dealers&rdquo;
                </span>{" "}
                can login
                <br />
                using their mobile number
              </p>

              {/* Brand tagline */}
              <p className="mt-4 text-2xl sm:text-3xl font-bold text-gray-900 font-['DM_Sans'] tracking-tight">
                Friends OnePlace
              </p>
              <p className="mt-0.5 text-base text-orange-500 font-['DM_Sans'] italic">
                – the all-in-one hub
              </p>

              {!showOTP && (
                <form
                  className="mt-8 space-y-5"
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleSendOTP();
                  }}
                >
                  <input
                    type="text"
                    placeholder="Enter Mobile Number"
                    maxLength={10}
                    value={mobile}
                    onChange={(e) =>
                      setMobile(e.target.value.replace(/\D/g, ""))
                    }
                    className="w-full px-5 py-4 text-lg border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent transition"
                    disabled={loading}
                  />

                  <button
                    type="submit"
                    disabled={loading || mobile.length !== 10}
                    className="w-full bg-gray-800 text-white font-medium py-4 rounded-xl hover:bg-gray-900 disabled:bg-gray-400 disabled:cursor-not-allowed transition transform active:scale-95"
                  >
                    {loading ? "Sending OTP… (may take 20-30 sec)" : "Send OTP"}
                  </button>

                  <div className="flex items-center gap-3 pt-1">
                    <span className="h-px flex-1 bg-gray-200" />
                    <span className="text-xs text-gray-400 font-medium">OR</span>
                    <span className="h-px flex-1 bg-gray-200" />
                  </div>

                  <button
                    type="button"
                    onClick={() => setGstPopupOpen(true)}
                    className="w-full flex items-center justify-center gap-1.5 border-2 border-gray-800 text-gray-800 font-bold py-3 rounded-xl hover:bg-gray-800 hover:text-white transition active:scale-95"
                  >
                    Not registered? Search your GST
                    <span aria-hidden>→</span>
                  </button>
                </form>
              )}

              {showOTP && (
                <form
                  className="mt-8 space-y-6"
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleVerifyOTP();
                  }}
                >
                  <p className="text-gray-700 font-medium">
                    OTP sent to{" "}
                    <span className="font-bold text-gray-900">{mobile}</span>
                  </p>

                  <div className="flex justify-center gap-1 sm:gap-3">
                    {[0, 1, 2, 3].map((i) => (
                      <input
                        key={i}
                        ref={(el) => (inputRefs.current[i] = el)}
                        type="text"
                        inputMode="numeric"
                        maxLength="1"
                        value={otpInputs[i]}
                        onChange={(e) => handleOtpChange(e.target.value, i)}
                        onKeyDown={(e) => handleKeyDown(e, i)}
                        onPaste={(e) => handleOtpPaste(e, i)}
                        className="w-12 h-12 sm:w-14 sm:h-14 text-xl sm:text-2xl font-bold text-center border-2 border-gray-700 rounded-xl focus:border-gray-900 focus:outline-none transition-all"
                      />
                    ))}
                  </div>

                  <button
                    type="submit"
                    className="w-full bg-gray-800 text-white font-medium py-4 rounded-xl hover:bg-gray-900 transition transform active:scale-95"
                  >
                    Verify OTP
                  </button>

                  <p className="text-sm text-gray-600">
                    Didn’t receive OTP?{" "}
                    <span
                      onClick={handleResend}
                      className="font-semibold text-gray-800 cursor-pointer hover:underline"
                    >
                      Resend
                    </span>
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>

      {loading && (
        <div className="fixed inset-0 z-[999] bg-white/50 flex flex-col items-center justify-center">
          <video
            src="/materialwaveloading.webm"
            autoPlay
            loop
            muted
            playsInline
            className="w-32 h-32 sm:w-40 sm:h-40"
          />
          <p className="text-sm sm:text-base bg-green-500 text-white tracking-wide p-4 rounded-[10px]">
            Check your WhatsApp for OTP
          </p>
        </div>
      )}

      {showBypassOtpPopup && mobile === BYPASS_PHONE && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-2xl shadow-2xl p-7 max-w-sm w-full text-center animate-bounce-in">
            <h3 className="text-xl font-bold text-gray-800 mb-2">
              Test OTP (Bypass Login)
            </h3>
            <p className="text-gray-600 mb-5">
              For mobile <span className="font-bold">{BYPASS_PHONE}</span>
            </p>

            <div className="text-3xl font-extrabold tracking-widest bg-gray-100 rounded-xl py-4 mb-6">
              {BYPASS_OTP}
            </div>

            <button
              onClick={() => setShowBypassOtpPopup(false)}
              className="w-full bg-gray-800 text-white font-medium py-3 rounded-xl hover:bg-gray-900 transition"
            >
              OK
            </button>
          </div>
        </div>
      )}

      {showSuccessPopup && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-sm w-full text-center animate-bounce-in">
            <div className="w-20 h-20 bg-green-100 rounded-full mx-auto mb-6 flex items-center justify-center">
              <img
                src="/images/checkmark.gif"
                alt="Success"
                className="w-16 h-16"
              />
            </div>
            <h3 className="text-2xl font-bold text-gray-800 mb-3">
              Login Successful!
            </h3>
            <p className="text-gray-600 mb-2">Redirecting to dashboard...</p>
          </div>
        </div>
      )}

      <style>{`
        @keyframes float {
          0%,
          100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-12px);
          }
        }
        @keyframes bounce-in {
          0% {
            transform: scale(0.8);
            opacity: 0;
          }
          60% {
            transform: scale(1.05);
          }
          100% {
            transform: scale(1);
            opacity: 1;
          }
        }
        .animate-float {
          animation: float 4s ease-in-out infinite;
        }
        .animate-bounce-in {
          animation: bounce-in 0.6s ease-out;
        }
      `}</style>

      <GstSearchPopup
        open={gstPopupOpen}
        onClose={() => setGstPopupOpen(false)}
        mobile={mobile}
        onRegister={(gst) => {
          setGstPopupOpen(false);
          navigate("/registration-kyc", { state: { gst, contactNo: mobile } });
        }}
      />
    </>
  );
}
